import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { GetRatingsList, createGlobalRating } from "../../../store/action";
import { apiCall } from "../../../utils/action";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputBase from "@mui/material/InputBase";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import ColorPicker from "react-pick-color";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { hexToRgb, RATING_COLOR_PRESETS, rgbToHex } from "./ratingColorUtils";
import { scrollElementIntoContainerView } from "../../../utils/services";
import "../../../../assets/styles/containers/_addGlobalRating.scss";

const DEFAULT_NEW_RATING_COLOR = "#E89623";

const mapRatingFromApi = (rating) => ({
    id: rating.id,
    name: rating.name,
    definition: rating.definition,
    inSummary: !!rating.include_in_summary,
    used: (rating.comments_count || 0) > 0,
    color: rating.color
});

// Mirrors CLRatings.js's local ordering so ratings picked here land in the template in the
// same order they're shown in the Comment Library's Ratings tab (the API itself has no order field).
const getPriorityKey = (companyID) => `ratings_priority_${companyID}`;
const loadPriorityIds = (companyID) => {
    try {
        return JSON.parse(localStorage.getItem(getPriorityKey(companyID))) || [];
    } catch (e) {
        return [];
    }
};
// Anything outside the priority list is kept in creation order (ascending id) so a rating created
// here shows up at the bottom of the table on a later open, not back at the top.
const byCreationOrder = (a, b) => (parseInt(a?.id) || 0) - (parseInt(b?.id) || 0);
const applyPriorityOrder = (data, priorityIds) => {
    if (!priorityIds || priorityIds.length === 0) return [...data].sort(byCreationOrder);
    const prioritySet = new Set(priorityIds.map(String));
    const prioritized = priorityIds.map((pid) => data.find((r) => String(r.id) === String(pid))).filter(Boolean);
    const rest = data.filter((r) => !prioritySet.has(String(r.id))).sort(byCreationOrder);
    return [...prioritized, ...rest];
};

// standalone=true renders just the "create a rating" form as a plain dialog (no browse/select-existing
// table, no per-template attach step) — used e.g. from the Ratings tab, where there's no template to
// attach ratings into. standalone=false (default) keeps the original browse+select modal with its
// inline "Add new rating" drawer, used from the template editor.
function AddGlobalRating({ open, onClose, onAddRatings, standalone = false, onCreated, existingRatings = [], existingRatingIds = [] }) {
    const dispatch = useDispatch();
    const [activeFilter, setActiveFilter] = useState("All");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [ratingsList, setRatingsList] = useState([]);
    const [isLoadingRatings, setIsLoadingRatings] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    // The wrapper is the scroll container — the body inside it isn't scrollable on its own.
    const tableWrapperRef = useRef(null);
    const tableHeaderRef = useRef(null);
    const [scrollToRatingId, setScrollToRatingId] = useState(null);

    const fetchRatings = async () => {
        setIsLoadingRatings(true);
        try {
            const response = await dispatch(GetRatingsList());
            const data = response?.data?.data || [];
            const companyID = sessionStorage.getItem("company-key");
            const ordered = applyPriorityOrder(data, loadPriorityIds(companyID));
            setRatingsList(ordered.map(mapRatingFromApi));
        } catch (err) {
            console.error("Ratings API error:", err);
        } finally {
            setIsLoadingRatings(false);
        }
    };

    useEffect(() => {
        if (open && !standalone) fetchRatings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, standalone]);

    const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
    const [isSavingNewRating, setIsSavingNewRating] = useState(false);
    const [newRatingName, setNewRatingName] = useState("");
    const [newRatingNameError, setNewRatingNameError] = useState("");
    const [newRatingDefinition, setNewRatingDefinition] = useState("");
    const [flagForSummary, setFlagForSummary] = useState(false);
    const [newRatingColor, setNewRatingColor] = useState(DEFAULT_NEW_RATING_COLOR);
    const [defaultRatingColors, setDefaultRatingColors] = useState([]);
    const [hexInput, setHexInput] = useState(DEFAULT_NEW_RATING_COLOR.replace("#", ""));
    const rgb = useMemo(() => hexToRgb(newRatingColor), [newRatingColor]);

    useEffect(() => {
        setHexInput(newRatingColor.replace("#", "").toUpperCase());
    }, [newRatingColor]);

    const handleHexInputChange = (raw) => {
        const digits = raw.replace(/[^0-9A-Fa-f]/g, "").slice(0, 6).toUpperCase();
        setHexInput(digits);
        if (digits.length === 6) setNewRatingColor(`#${digits}`);
    };

    const handleRgbChannelChange = (channel, value) => {
        const next = { ...rgb, [channel]: Math.max(0, Math.min(255, parseInt(value, 10) || 0)) };
        setNewRatingColor(rgbToHex(next.r, next.g, next.b));
    };

    const getDefaultRatingColors = () => {
        const formdata = {
            company_id: sessionStorage.getItem("company_id"),
            user_id: sessionStorage.getItem("user_id"),
            role_id: sessionStorage.getItem("role_id"),
        };
        apiCall(
            (response) => {
                const { data, success } = response.data;
                if (success) {
                    setDefaultRatingColors((data || []).map((rating) => rating.rating_color_code));
                }
            },
            formdata,
            "GET_DEFAULT_RATING_COLOR"
        );
    };

    const filteredRatings = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        const filtered = ratingsList.filter((rating) => {
            const matchesSearch = !query || rating.name.toLowerCase().includes(query) || (rating.definition || "").toLowerCase().includes(query);
            const matchesFilter =
                activeFilter === "All" ||
                (activeFilter === "Used" && rating.used) ||
                (activeFilter === "Unused" && !rating.used) ||
                (activeFilter === "Summary" && rating.inSummary);
            return matchesSearch && matchesFilter;
        });

        const { key, direction } = sortConfig;
        if (!key) return filtered;

        const sorted = [...filtered].sort((a, b) => {
            let aValue = a[key];
            let bValue = b[key];

            if (typeof aValue === "boolean" || typeof bValue === "boolean") {
                aValue = aValue ? 1 : 0;
                bValue = bValue ? 1 : 0;
            } else {
                aValue = String(aValue).toLowerCase();
                bValue = String(bValue).toLowerCase();
            }

            if (aValue < bValue) return direction === "asc" ? -1 : 1;
            if (aValue > bValue) return direction === "asc" ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [searchTerm, activeFilter, ratingsList, sortConfig]);

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
    };

    const renderSortIcon = (key) => (
        <UnfoldMoreIcon className={`ratings-sort-icon${sortConfig.key === key ? " ratings-sort-icon-active" : ""}`} />
    );

    const isAlreadyAdded = (ratingId) => existingRatingIds.includes(ratingId);

    const toggleRow = (id) => {
        if (isAlreadyAdded(id)) return;
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
    };

    const selectableRatings = filteredRatings.filter((rating) => !isAlreadyAdded(rating.id));
    const allVisibleSelected = selectableRatings.length > 0 && selectableRatings.every((rating) => selectedIds.includes(rating.id));

    const toggleSelectAll = () => {
        const visibleIds = selectableRatings.map((rating) => rating.id);
        if (allVisibleSelected) {
            setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
        } else {
            setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
        }
    };

    const handleClose = () => {
        setSearchTerm("");
        setActiveFilter("All");
        setSelectedIds([]);
        onClose();
    };

    const handleAddSelectedRatings = async () => {
        const selectedRatings = ratingsList.filter((rating) => selectedIds.includes(rating.id));
        if (selectedRatings.length === 0) {
            handleClose();
            return;
        }
        setIsSubmitting(true);
        try {
            await onAddRatings?.(selectedRatings);
            handleClose();
        } catch (err) {
            console.error("Add ratings error:", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetAddRatingForm = () => {
        setNewRatingName("");
        setNewRatingNameError("");
        setNewRatingDefinition("");
        setFlagForSummary(false);
        setNewRatingColor(DEFAULT_NEW_RATING_COLOR);
    };

    const handleOpenAddDrawer = () => {
        resetAddRatingForm();
        setIsAddDrawerOpen(true);
        if (defaultRatingColors.length === 0) getDefaultRatingColors();
    };

    const handleCloseAddDrawer = () => {
        setIsAddDrawerOpen(false);
        resetAddRatingForm();
    };

    useEffect(() => {
        if (standalone && open) {
            resetAddRatingForm();
            if (defaultRatingColors.length === 0) getDefaultRatingColors();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [standalone, open]);

    // The create API doesn't always return the new rating's id, and an id-less rating ends up
    // serialized as null in the template's rating_data payload — so fall back to looking the
    // rating up by name in a freshly fetched list.
    const resolveCreatedRatingId = async (result, trimmedName, knownIds) => {
        const returnedId = parseInt(result?.data?.id ?? result?.id);
        if (returnedId) return returnedId;
        const listResponse = await dispatch(GetRatingsList());
        const created = [...(listResponse?.data?.data || [])]
            .reverse()
            .find(
                (rating) =>
                    (rating.name || "").trim().toLowerCase() === trimmedName.toLowerCase() &&
                    !knownIds.includes(String(rating.id))
            );
        return parseInt(created?.id);
    };

    const handleSaveNewRating = async () => {
        const trimmedName = newRatingName.trim();
        if (!trimmedName) return;

        if (standalone) {
            const nameExists = existingRatings?.some(
                (rating) => (rating?.name || "").trim().toLowerCase() === trimmedName.toLowerCase()
            );
            if (nameExists) {
                setNewRatingNameError("A rating with this name already exists.");
                return;
            }
        }

        setIsSavingNewRating(true);
        try {
            const result = await dispatch(
                createGlobalRating({
                    name: trimmedName,
                    color: newRatingColor,
                    definition: newRatingDefinition.trim(),
                    include_in_summary: flagForSummary ? 1 : 0,
                })
            );

            const knownIds = (standalone ? existingRatings : ratingsList).map((rating) => String(rating?.id));
            const newRatingId = await resolveCreatedRatingId(result, trimmedName, knownIds);
            if (!newRatingId) {
                throw new Error("Failed to determine the id of the newly created rating");
            }

            const newRating = {
                id: newRatingId,
                name: trimmedName,
                definition: newRatingDefinition.trim(),
                inSummary: flagForSummary,
                used: false,
                color: newRatingColor
            };

            if (standalone) {
                // The caller toasts here — it owns the refetch that follows — so this branch stays quiet.
                onCreated?.(newRating);
                onClose();
            } else {
                toast.success("Rating has been added");
                // Appended, not prepended: a new rating belongs at the end of the list, and the
                // picker's order is also the order the selected ratings are added to the template
                // in. Search/filter/sort are reset so the new row is actually in the rendered list,
                // and the effect below scrolls it into view once it has rendered.
                setRatingsList((prev) => [...prev, newRating]);
                setSelectedIds((prev) => [...prev, newRating.id]);
                setSearchTerm("");
                setActiveFilter("All");
                setSortConfig({ key: null, direction: "asc" });
                setScrollToRatingId(newRating.id);
                handleCloseAddDrawer();
            }
        } catch (err) {
            toast.error(err.message || "Failed to create rating");
        } finally {
            setIsSavingNewRating(false);
        }
    };

    // Scrolls the row of a just-created rating into view. It runs off filteredRatings so it waits
    // for the row to be rendered, and offsets the sticky header that sits inside the scroll area.
    useEffect(() => {
        if (!scrollToRatingId) return;
        const frame = requestAnimationFrame(() => {
            const wrapper = tableWrapperRef.current;
            const row = wrapper?.querySelector(`[data-rating-id="${scrollToRatingId}"]`);
            if (!row) return;
            scrollElementIntoContainerView(wrapper, row, tableHeaderRef.current?.offsetHeight || 0);
            setScrollToRatingId(null);
        });
        return () => cancelAnimationFrame(frame);
    }, [scrollToRatingId, filteredRatings]);

    // The pieces below are shared, but the two surfaces arrange them differently: the template
    // editor's side sheet keeps its original single-column stack, while the Ratings tab dialog uses
    // the two-column layout from the design.
    const previewBlock = (
        <div className="add-rating-preview">
            <span className="add-rating-preview-label">Preview</span>
            <div className="add-rating-preview-content">
                <span className="add-rating-preview-swatch" style={{ background: newRatingColor }} />
                <span className="add-rating-preview-name">{newRatingName || "Rating name"}</span>
            </div>
        </div>
    );

    const nameField = (
        <div className="add-rating-field">
            <label className="add-rating-label">Name</label>
            <TextField
                fullWidth
                size="small"
                variant="outlined"
                className="add-rating-input"
                value={newRatingName}
                onChange={(e) => setNewRatingName(e.target.value)}
                error={!!newRatingNameError}
                helperText={newRatingNameError}
            />
        </div>
    );

    const definitionField = (
        <div className="add-rating-field">
            <label className="add-rating-label">Definition</label>
            <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                variant="outlined"
                className="add-rating-input add-rating-input-resizable"
                value={newRatingDefinition}
                onChange={(e) => setNewRatingDefinition(e.target.value)}
            />
        </div>
    );

    const renderFlagSection = (label) => (
        <div className="add-rating-flag-section">
            <div className="add-rating-flag-row">
                <Switch
                    className="add-rating-flag-switch"
                    checked={flagForSummary}
                    onChange={(e) => setFlagForSummary(e.target.checked)}
                />
                <span className="add-rating-flag-label">{label}</span>
            </div>
            <p className="add-rating-flag-helper">
                Any comment or line with this rating assigned will be included in the report summary.
            </p>
        </div>
    );

    const colorPicker = (
        <ColorPicker
            hideAlpha
            hideInputs
            color={newRatingColor}
            className="rating-color-picker"
            onChange={(color) => setNewRatingColor(color.hex)}
            theme={{
                background: "transparent",
                borderColor: "transparent",
                boxShadow: "none"
            }}
        />
    );

    const colorFields = (
        <div className="rating-color-fields">
            <div className="rating-color-field">
                <label className="rating-color-field-label">Hex</label>
                <div className="rating-color-field-input rating-color-field-hex">
                    <span className="rating-color-field-prefix">#</span>
                    <input
                        type="text"
                        maxLength={6}
                        value={hexInput}
                        onChange={(e) => handleHexInputChange(e.target.value)}
                    />
                </div>
            </div>
            <div className="rating-color-field">
                <label className="rating-color-field-label">R</label>
                <div className="rating-color-field-input">
                    <input
                        type="number"
                        min={0}
                        max={255}
                        value={rgb.r}
                        onChange={(e) => handleRgbChannelChange("r", e.target.value)}
                    />
                </div>
            </div>
            <div className="rating-color-field">
                <label className="rating-color-field-label">G</label>
                <div className="rating-color-field-input">
                    <input
                        type="number"
                        min={0}
                        max={255}
                        value={rgb.g}
                        onChange={(e) => handleRgbChannelChange("g", e.target.value)}
                    />
                </div>
            </div>
            <div className="rating-color-field">
                <label className="rating-color-field-label">B</label>
                <div className="rating-color-field-input">
                    <input
                        type="number"
                        min={0}
                        max={255}
                        value={rgb.b}
                        onChange={(e) => handleRgbChannelChange("b", e.target.value)}
                    />
                </div>
            </div>
        </div>
    );

    const colorPresets = (
        <div className="rating-color-presets">
            {RATING_COLOR_PRESETS.map((presetColor) => (
                <button
                    key={presetColor}
                    type="button"
                    className="rating-color-preset-swatch"
                    style={{ background: presetColor }}
                    onClick={() => setNewRatingColor(presetColor)}
                    aria-label={presetColor}
                />
            ))}
        </div>
    );

    // Template editor side sheet — the original single-column form.
    const drawerFormFields = (
        <>
            {nameField}
            {definitionField}
            {renderFlagSection("Include in report summary")}
            <div className="add-rating-color-section">
                {colorPicker}
                <div className="rating-color-side-column">
                    {colorFields}
                    {colorPresets}
                    {previewBlock}
                </div>
            </div>
        </>
    );

    // Ratings tab dialog — the two-column layout from the design.
    const standaloneFormFields = (
        <div className="add-rating-form">
            <div className="add-rating-form-main">
                {previewBlock}
                {nameField}
                {definitionField}
                {renderFlagSection("Include in report summary")}
            </div>
            <div className="add-rating-form-color">
                {colorPicker}
                {colorFields}
                {colorPresets}
            </div>
        </div>
    );

    if (standalone) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="ratings-modal-container add-rating-standalone-modal">
                <div className="ratings-modal-header">
                    <Typography className="ratings-modal-title">Add rating</Typography>
                    <CloseOutlinedIcon className="ratings-modal-close" onClick={onClose} />
                </div>
                <DialogContent className="add-rating-drawer-content add-rating-standalone-content">
                    {standaloneFormFields}
                </DialogContent>
                <DialogActions className="add-rating-drawer-footer">
                    <Button className="ratings-cancel-btn" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button className="ratings-submit-btn" onClick={handleSaveNewRating} disabled={isSavingNewRating}>
                        {isSavingNewRating ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Add new rating"}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" maxHeight="100%" fullWidth className="ratings-modal-container">
            <div className="ratings-modal-header">
                <Typography className="ratings-modal-title">Global Ratings</Typography>
                <CloseOutlinedIcon className="ratings-modal-close" onClick={handleClose} />
            </div>
            <DialogContent className="ratings-modal-content">
                <Grid container className="ratings-modal-toolbar">
                    <Grid item className="ratings-search-wrapper">
                        <SearchIcon className="ratings-search-icon" />
                        <InputBase
                            className="ratings-search-input"
                            placeholder="Search in table"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Grid>
                    <Grid item className="ratings-toolbar-spacer" />
                    <Grid item>
                        <Button className="ratings-add-new-btn" startIcon={<AddIcon />} onClick={handleOpenAddDrawer}>
                            Add new
                        </Button>
                    </Grid>
                </Grid>
                <div className="ratings-table-wrapper" ref={tableWrapperRef}>
                    <div className="ratings-table-header" ref={tableHeaderRef}>
                        <div className="ratings-cell ratings-cell-checkbox">
                            <FormControlLabel
                                className="ratings-checkbox-label"
                                label=""
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={allVisibleSelected}
                                        onChange={toggleSelectAll}
                                        inputProps={{ "aria-label": "Select all ratings" }}
                                    />
                                }
                            />
                        </div>
                        <div className="ratings-cell ratings-cell-name ratings-cell-sortable" onClick={() => handleSort("name")}>
                            Name
                            {renderSortIcon("name")}
                        </div>
                        <div
                            className="ratings-cell ratings-cell-definition ratings-cell-sortable"
                            onClick={() => handleSort("definition")}
                        >
                            Definition
                            {renderSortIcon("definition")}
                        </div>
                        <div
                            className="ratings-cell ratings-cell-summary ratings-cell-sortable"
                            onClick={() => handleSort("inSummary")}
                        >
                            In summary
                            {renderSortIcon("inSummary")}
                        </div>
                        <div className="ratings-cell ratings-cell-color ratings-cell-sortable" onClick={() => handleSort("color")}>
                            Color
                            {renderSortIcon("color")}
                        </div>
                    </div>
                    <div className="ratings-table-body">
                        {isLoadingRatings ? (
                            <div className="ratings-empty-state">Loading ratings...</div>
                        ) : (
                            <>
                                {filteredRatings.map((rating) => {
                                    const alreadyAdded = isAlreadyAdded(rating.id);
                                    return (
                                        <div
                                            className={`ratings-table-row${alreadyAdded ? " ratings-table-row-disabled" : ""}`}
                                            key={rating.id}
                                            data-rating-id={rating.id}
                                        >
                                            <div className="ratings-cell ratings-cell-checkbox">
                                                {/* The label fills the cell so the whole hit area toggles the row —
                                                    on its own the checkbox shows a hover ripple wider than the part
                                                    that actually responds to a click. */}
                                                <FormControlLabel
                                                    className="ratings-checkbox-label"
                                                    label=""
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={alreadyAdded || selectedIds.includes(rating.id)}
                                                            disabled={alreadyAdded}
                                                            onChange={() => toggleRow(rating.id)}
                                                            inputProps={{ "aria-label": `Select ${rating.name}` }}
                                                        />
                                                    }
                                                />
                                            </div>
                                            <div className="ratings-cell ratings-cell-name">{rating.name}</div>
                                            <div className="ratings-cell ratings-cell-definition">{rating.definition}</div>
                                            <div className="ratings-cell ratings-cell-summary">
                                                {rating.inSummary && <CheckIcon className="ratings-summary-check" />}
                                            </div>
                                            <div className="ratings-cell ratings-cell-color">
                                                <span className="ratings-color-swatch" style={{ background: rating.color }} />
                                                <span className="ratings-color-hex">{rating.color}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                                {filteredRatings.length === 0 && <div className="ratings-empty-state">No ratings found</div>}
                            </>
                        )}
                    </div>
                </div>
            </DialogContent>
            <DialogActions className="ratings-modal-actions">
                <Button className="ratings-edit-link" startIcon={<OpenInNewIcon />} onClick={() => window.open("/#/library/?tab=ratings", "_blank")}>
                    Edit ratings
                </Button>
                <div className="ratings-actions-spacer" />
                <Button className="ratings-cancel-btn" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    className="ratings-submit-btn"
                    onClick={handleAddSelectedRatings}
                    disabled={isSubmitting || selectedIds.length === 0}
                >
                    {selectedIds.length > 0 ? `Add (${selectedIds.length}) ratings` : "Add ratings"}
                </Button>
            </DialogActions>

            <div
                className={`add-rating-backdrop${isAddDrawerOpen ? " add-rating-backdrop-open" : ""}`}
                onClick={handleCloseAddDrawer}
            />
            <div className={`add-rating-panel${isAddDrawerOpen ? " add-rating-panel-open" : ""}`}>
                <div className="add-rating-drawer-header">
                    <Typography className="add-rating-drawer-title">Add new rating</Typography>
                </div>
                <div className="add-rating-drawer-content">
                    {drawerFormFields}
                </div>
                <div className="add-rating-drawer-footer">
                    <Button className="ratings-cancel-btn" onClick={handleCloseAddDrawer}>
                        Cancel
                    </Button>
                    <Button className="ratings-submit-btn" onClick={handleSaveNewRating} disabled={isSavingNewRating}>
                        {isSavingNewRating ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save"}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

export default AddGlobalRating;